import React from 'react';
import type {
  EpicDetail,
  MigrationClassification,
  StorySummary,
} from '../../../../../../shared/types/epics';
import { StatusBadge, type StatusTone } from '../../../../components/ui';
import { pct } from '../format';

// ---------------------------------------------------------------------------
// FORGE-276 — legacy .NET migration progress. Epic-level panel summarising the
// strangler-fig migration: how many projects are migrated (stories TESTED),
// how many are hard-blocked (PAUSED_NEEDS_HUMAN — WCF/WebForms), how many
// remain, plus a per-project list with classification, difficulty, and a
// parity ✓/✗ (or "needs human") status. Mirrors SafetyNetPanel styling.
// Rendered only for migration epics (epicKind === 'migration') or when any
// story carries migration fields, so ordinary epics show nothing.
// ---------------------------------------------------------------------------

/** Distinct tone per migration classification (mechanical/assisted/human-required). */
const MIGRATION_CLASS_TONE: Record<MigrationClassification, StatusTone> = {
  mechanical: 'success',
  assisted: 'warning',
  'human-required': 'danger',
};
const MIGRATION_CLASS_LABEL: Record<MigrationClassification, string> = {
  mechanical: 'mechanical',
  assisted: 'assisted',
  'human-required': 'human-required',
};

/** True when a story carries any migration field (so a mixed epic still shows it). */
function storyHasMigration(s: StorySummary): boolean {
  return (
    s.migrationProject !== undefined ||
    s.migrationClassification !== undefined ||
    s.migrationDifficulty !== undefined ||
    s.migrationParity !== undefined
  );
}

/** One project's migration row — name, classification badge, difficulty, parity/status. */
const MigrationRow: React.FC<{ story: StorySummary }> = ({ story }) => {
  const cls = story.migrationClassification;
  const done = story.state === 'TESTED';
  const paused = story.state === 'PAUSED_NEEDS_HUMAN';
  const humanRequired = paused || cls === 'human-required';
  return (
    <div
      className="flex items-center justify-between gap-2 py-1.5"
      data-testid="migration-project"
    >
      <div className="min-w-0 flex items-center gap-2">
        <span
          className="truncate font-mono text-xs text-slate-700"
          title={story.migrationProject ?? story.title}
        >
          {story.migrationProject || story.title || story.id}
        </span>
        {cls && (
          <StatusBadge tone={MIGRATION_CLASS_TONE[cls]}>
            {MIGRATION_CLASS_LABEL[cls]}
          </StatusBadge>
        )}
        {typeof story.migrationDifficulty === 'number' && (
          <span className="shrink-0 tabular-nums text-[11px] text-slate-400">
            difficulty {pct(story.migrationDifficulty)}
          </span>
        )}
      </div>
      <span className="shrink-0 tabular-nums text-xs">
        {humanRequired ? (
          <StatusBadge tone="danger">needs human</StatusBadge>
        ) : story.migrationParity === true ? (
          <span className="font-semibold text-emerald-600" title="parity with Framework baseline">
            ✓ parity
          </span>
        ) : story.migrationParity === false ? (
          <span className="font-semibold text-rose-700" title="behaviour diverged from Framework baseline">
            ✗ parity
          </span>
        ) : done ? (
          <StatusBadge tone="success">done</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">pending</StatusBadge>
        )}
      </span>
    </div>
  );
};

export const MigrationPanel: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  const migrationStories = epic.stories.filter(storyHasMigration);
  const isMigrationEpic = epic.epicKind === 'migration';
  // Render nothing unless this is a migration epic or a story carries migration data.
  if (!isMigrationEpic && migrationStories.length === 0) return null;

  // A migration epic with no stamped stories yet still shows the header + zeroes.
  const rows = migrationStories.length > 0 ? migrationStories : [];
  const total = rows.length;
  const migrated = rows.filter((s) => s.state === 'TESTED').length;
  const paused = rows.filter((s) => s.state === 'PAUSED_NEEDS_HUMAN').length;
  const remaining = Math.max(total - migrated - paused, 0);

  const sorted = [...rows].sort((a, b) =>
    (a.migrationProject || a.title || a.id).localeCompare(
      b.migrationProject || b.title || b.id,
    ),
  );

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50" data-testid="migration-panel">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <span className="text-sm font-semibold text-slate-900">🏗️ Migration progress</span>
        <StatusBadge tone={migrated === total && total > 0 ? 'success' : 'neutral'}>
          {migrated} of {total} migrated
        </StatusBadge>
        {paused > 0 && (
          <StatusBadge tone="danger">{paused} need human</StatusBadge>
        )}
        {remaining > 0 && (
          <StatusBadge tone="info">{remaining} remaining</StatusBadge>
        )}
      </div>
      {sorted.length > 0 && (
        <div className="border-t border-slate-200 px-3 py-1.5 divide-y divide-slate-100">
          {sorted.map((s) => (
            <MigrationRow key={s.id} story={s} />
          ))}
        </div>
      )}
    </div>
  );
};
